/**
 * UIStore - MobX store for UI state management.
 *
 * Manages layout state, view state, and user UI preferences.
 * Persists state to localStorage for consistency across sessions.
 */

import { makeAutoObservable, runInAction } from 'mobx';

import type { RootStore } from './RootStore';

/** LocalStorage key for UI state */
const UI_STATE_KEY = 'idle-depths-ui-state';

/**
 * Bank tab category type.
 */
export type BankTabCategory =
  | 'all'
  | 'weapon'
  | 'armor'
  | 'resource'
  | 'consumable'
  | 'quest'
  | 'misc';

/**
 * Persisted UI state shape.
 */
interface PersistedUIState {
  sidebarCollapsed: boolean;
  actionBarCollapsed: boolean;
  quickActions: string[];
  soundEnabled: boolean;
  lastVisitedSkill: string | null;
  bankSelectedTab: BankTabCategory;
}

/**
 * UIStore manages all UI-related state.
 *
 * @example
 * ```tsx
 * const { uiStore } = useRootStore();
 * uiStore.toggleSidebar();
 * ```
 */
export class UIStore {
  // =========================================
  // Layout State
  // =========================================

  /** Whether the sidebar is collapsed */
  sidebarCollapsed = false;

  /** Whether the action bar is collapsed to a small button */
  actionBarCollapsed = false;

  /** Whether the action bar is completely hidden */
  actionBarMinimized = false;

  // =========================================
  // View State
  // =========================================

  /** Currently active dialog (null if none open) */
  activeDialog: string | null = null;

  /** Current main view identifier */
  activeView = 'dashboard';

  /** Last skill view the user visited */
  lastVisitedSkill: string | null = null;

  /** Selected bank tab category */
  bankSelectedTab: BankTabCategory = 'all';

  /** Selected item ID in bank (for detail view) */
  bankSelectedItemId: string | null = null;

  // =========================================
  // Quick Actions
  // =========================================

  /** User-configured quick action IDs */
  quickActions: string[] = [];

  // =========================================
  // Preferences
  // =========================================

  /** Whether sound effects are enabled */
  soundEnabled = true;

  /** Whether notifications are temporarily paused */
  notificationPaused = false;

  constructor(_rootStore: RootStore) {
    makeAutoObservable(this);
    this.loadFromStorage();
  }

  // =========================================
  // Persistence
  // =========================================

  /**
   * Load UI state from localStorage.
   */
  private loadFromStorage(): void {
    try {
      const saved = localStorage.getItem(UI_STATE_KEY);
      if (saved) {
        const data = JSON.parse(saved) as Partial<PersistedUIState>;
        runInAction(() => {
          if (typeof data.sidebarCollapsed === 'boolean') {
            this.sidebarCollapsed = data.sidebarCollapsed;
          }
          if (typeof data.actionBarCollapsed === 'boolean') {
            this.actionBarCollapsed = data.actionBarCollapsed;
          }
          if (Array.isArray(data.quickActions)) {
            this.quickActions = data.quickActions;
          }
          if (typeof data.soundEnabled === 'boolean') {
            this.soundEnabled = data.soundEnabled;
          }
          if (typeof data.lastVisitedSkill === 'string' || data.lastVisitedSkill === null) {
            this.lastVisitedSkill = data.lastVisitedSkill;
          }
          if (typeof data.bankSelectedTab === 'string') {
            this.bankSelectedTab = data.bankSelectedTab;
          }
        });
      }
    } catch (error) {
      console.warn('Failed to load UI state from localStorage:', error);
    }
  }

  /**
   * Save UI state to localStorage.
   */
  private saveToStorage(): void {
    try {
      const state: PersistedUIState = {
        sidebarCollapsed: this.sidebarCollapsed,
        actionBarCollapsed: this.actionBarCollapsed,
        quickActions: this.quickActions,
        soundEnabled: this.soundEnabled,
        lastVisitedSkill: this.lastVisitedSkill,
        bankSelectedTab: this.bankSelectedTab,
      };
      localStorage.setItem(UI_STATE_KEY, JSON.stringify(state));
    } catch (error) {
      console.warn('Failed to save UI state to localStorage:', error);
    }
  }

  // =========================================
  // Sidebar Actions
  // =========================================

  /**
   * Toggle sidebar collapsed state.
   */
  toggleSidebar(): void {
    this.sidebarCollapsed = !this.sidebarCollapsed;
    this.saveToStorage();
  }

  /**
   * Set sidebar collapsed state.
   */
  setSidebarCollapsed(collapsed: boolean): void {
    this.sidebarCollapsed = collapsed;
    this.saveToStorage();
  }

  // =========================================
  // Action Bar Actions
  // =========================================

  /**
   * Toggle action bar collapsed state (collapsed = small button in corner).
   */
  toggleActionBarCollapse(): void {
    this.actionBarCollapsed = !this.actionBarCollapsed;
    this.saveToStorage();
  }

  /**
   * Set action bar collapsed state.
   */
  setActionBarCollapsed(collapsed: boolean): void {
    this.actionBarCollapsed = collapsed;
    this.saveToStorage();
  }

  /**
   * Toggle action bar minimized state (minimized = completely hidden).
   */
  toggleActionBarMinimize(): void {
    this.actionBarMinimized = !this.actionBarMinimized;
  }

  /**
   * Set action bar minimized state.
   */
  setActionBarMinimized(minimized: boolean): void {
    this.actionBarMinimized = minimized;
  }

  // =========================================
  // Dialog Actions
  // =========================================

  /**
   * Open a dialog by ID.
   */
  openDialog(dialogId: string): void {
    this.activeDialog = dialogId;
  }

  /**
   * Close the currently active dialog.
   */
  closeDialog(): void {
    this.activeDialog = null;
  }

  // =========================================
  // Bank Actions
  // =========================================

  /**
   * Set the bank selected tab.
   */
  setBankSelectedTab(tab: BankTabCategory): void {
    this.bankSelectedTab = tab;
    this.saveToStorage();
  }

  /**
   * Set the bank selected item (for detail view).
   */
  setBankSelectedItemId(itemId: string | null): void {
    this.bankSelectedItemId = itemId;
  }

  // =========================================
  // View Actions
  // =========================================

  /**
   * Set the active view.
   */
  setActiveView(view: string): void {
    this.activeView = view;
  }

  /**
   * Set the last visited skill for quick navigation.
   */
  setLastVisitedSkill(skillId: string | null): void {
    this.lastVisitedSkill = skillId;
    this.saveToStorage();
  }

  // =========================================
  // Quick Actions
  // =========================================

  /**
   * Add an action to quick actions.
   */
  addQuickAction(actionId: string): void {
    if (!this.quickActions.includes(actionId)) {
      this.quickActions.push(actionId);
      this.saveToStorage();
    }
  }

  /**
   * Remove an action from quick actions.
   */
  removeQuickAction(actionId: string): void {
    this.quickActions = this.quickActions.filter((id) => id !== actionId);
    this.saveToStorage();
  }

  /**
   * Set quick actions (replaces entire list).
   */
  setQuickActions(actionIds: string[]): void {
    this.quickActions = actionIds;
    this.saveToStorage();
  }

  // =========================================
  // Preferences Actions
  // =========================================

  /**
   * Toggle sound enabled state.
   */
  toggleSound(): void {
    this.soundEnabled = !this.soundEnabled;
    this.saveToStorage();
  }

  /**
   * Set sound enabled state.
   */
  setSoundEnabled(enabled: boolean): void {
    this.soundEnabled = enabled;
    this.saveToStorage();
  }

  /**
   * Toggle notification paused state.
   */
  toggleNotificationPause(): void {
    this.notificationPaused = !this.notificationPaused;
  }

  /**
   * Set notification paused state.
   */
  setNotificationPaused(paused: boolean): void {
    this.notificationPaused = paused;
  }

  // =========================================
  // Reset
  // =========================================

  /**
   * Reset UI state to defaults.
   */
  reset(): void {
    this.sidebarCollapsed = false;
    this.actionBarCollapsed = false;
    this.actionBarMinimized = false;
    this.activeDialog = null;
    this.activeView = 'dashboard';
    this.lastVisitedSkill = null;
    this.quickActions = [];
    this.soundEnabled = true;
    this.notificationPaused = false;
    this.bankSelectedTab = 'all';
    this.bankSelectedItemId = null;
    this.saveToStorage();
  }
}
